<script setup>
/**
 * FormControlModulestructureType
 * High-order type orchestration factory picker.
 * Pulls operational schema definitions asynchronously from backend configuration endpoints,
 * enforces safety rules to block mutations on saved records, and handles localized field overrides.
 */
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import FormControlPlainText from "./FormControlPlainText.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const route = proxy.$route;

// --- State Variables ---

const options = ref([]);
const isTeaserOrListing = ref(false);

// --- Synchronous Computeds ---

/**
 * Grabs the descriptive readable text name corresponding to the selected primary index
 * specifically when the input element falls into a read-only state.
 */
const readOnlyValue = computed(() => {
    if (!options.value || options.value.length === 0) return '';

    const activeValue = props.currentData?.[props.field.name];
    const match = options.value.find(obj => obj.value === activeValue);

    return match ? match.text : '';
});

const isFormComponent = computed(() => store.getters['componentFields/isForm']);
const isContentTypeComponent = computed(() => store.getters['componentFields/isContentType']);
const betaMode = computed(() => store.state.betaMode);

const component = computed(() => {
    const items = store.state.components?.items || [];
    const targetModuleId = props.currentData?.modulecomponentsID || props.currentData?.modulecomponentsId;
    return items.find(o => o.id === targetModuleId);
});

// --- Asynchronous Orchestrators ---

/**
 * Checks routing states to determine if the active model template
 * falls within standard listing or teaser UI representations.
 */
const fetchTeaserOrListingStatus = async () => {
    const isThemeEditor = route.name === 'themesEditor';
    const isContentTypesSection = route.query?.section === 'content_types';

    if (isThemeEditor && isContentTypesSection && route.query?.id) {
        try {
            const templateItem = await store.dispatch('itemData/get', {
                tableName: 'templates_templates',
                id: route.query.id
            });
            isTeaserOrListing.value = templateItem?.contentType === 'Listing' || templateItem?.contentType === 'Teaser';
        } catch (err) {
            console.error('Failed to resolve structural listing template properties:', err);
            isTeaserOrListing.value = false;
        }
        return;
    }
    isTeaserOrListing.value = false;
};

/**
 * Aggregates all global systemic backend configurations and filters the options array
 * dynamically based on active framework modes, routing contexts, and feature availability.
 */
const loadFieldTypesOptions = async () => {
    const isStructureComponent = route.name === 'form' && route.params?.component === 'modulestructure';

    try {
        const response = await store.dispatch('request/get', {
            url: 'api/get-components-structure-types'
        });

        let availableTypes = response?.data || [];
        const menuOptions = [{ value: '', text: 'Select…' }];

        if (!isStructureComponent) {
            // Apply structural segment filtering paths
            if (isFormComponent.value) {
                availableTypes = availableTypes.filter(o => o.form);
            } else if (isContentTypeComponent.value) {
                availableTypes = availableTypes.filter(o => o.contentType);

                // If template context isn't an aggregated teaser/listing layout, eject filters settings options
                if (!isTeaserOrListing.value) {
                    availableTypes = availableTypes.filter(o => o.type !== 'collectionFilters');
                }
            } else {
                availableTypes = availableTypes.filter(o => o.collection);
            }

            // Purge unreleased system configurations unless explicit preview overrides are flag-enabled
            availableTypes = availableTypes.filter(o => !o.beta || betaMode.value);
        }

        availableTypes.forEach((type) => {
            menuOptions.push({
                value: type.type,
                text: type.title
            });
        });

        options.value = menuOptions;
    } catch (err) {
        console.error('Critical failure retrieving structural control catalog properties:', err);
        options.value = [{ value: '', text: 'Select…' }];
    }
};

// --- Watchers & Initializer Lifecycle ---

/**
 * Safety-check Watcher: Listens for dynamic changes to the layout parameters.
 * Re-runs list processing workflows if underlying properties shift layout modes.
 */
watch([isFormComponent, isContentTypeComponent, betaMode, isTeaserOrListing], async () => {
    await loadFieldTypesOptions();
});

onMounted(async () => {
    if (props.field) {
        props.field.label = 'Type';

        // The field's type should never be changed. This is because each field has a specific data type so
        // changing it would result in data loss. It would also require extensive testing to ensure converting
        // between all data types worked.
        if (props.currentData?.id) {
            props.field.readonly = true;
        }
    }

    // Initialize state layers in parallel sequence
    await store.dispatch(`${props.formId}/setDefaultValue`, {
        name: props.field.name,
        value: ''
    });

    await fetchTeaserOrListingStatus();
    await loadFieldTypesOptions();
});
</script>

<template>
    <div class="form-control-modulestructure-type-container">

        <FormControlPlainText
            v-if="field.readonly"
            :value="readOnlyValue"
            :form-id="formId"
        />

        <select
            v-else-if="!field.readonly"
            :id="field.id"
            v-model="currentData[field.name]"
            :name="field.name"
            :required="field.required"
            class="form-select"
        >
            <option v-for="option in options" :key="option.value" :value="option.value">
                {{ option.text }}
            </option>
        </select>

    </div>
</template>